require('./Font.js');
const { isSpaceChar, splitWords, computeStroke, drawPaths } = require('./utils');

// Opentype functions
window._fillTextOpentype = function (text, x, y, justifyWidth, tracking) {
    var metrics;

    // If we have the metrics from this text there's no need to calculate them
    if (this.lineMetrics) {
        metrics = this.lineMetrics;
    } else {
        metrics = this.measureText(text);
    }

    // Get the path for each glyph
    var ffname = _getFontFamilyOpentype.call(this);
    var font = Font.hasFont(ffname) ? Font.resolve(ffname) : false;

    if (!font) {
        if (this._fillText) {
            return this._fillText.call(this, text, x, y, justifyWidth);
        }
        // Sometimes we get here and _fillText() is not available, so just do the normal call
        else {
            return this.fillText.call(this, text, x, y);
        }
    }

    const glyphOptions = {
        drawSVG: false,
        drawLayers: false,
        kerning: true,
        tracking: tracking,
        features: {
            liga: this.ligatures,
            rlig: this.ligatures
        }
    };
    var paths = font.getPaths(text, x, y, _getFontSizeOpentype.call(this), glyphOptions);

    // font.drawMetrics(this, text, x, y, _getFontSizeOpentype.call(this), glyphOptions);
    // font.drawPoints(this, text, x, y, _getFontSizeOpentype.call(this), glyphOptions);

    var spaceFound = false;
    var offset = 0;
    var extraSpace = 0;
    var justifiedApplied = false;
    var justifyNeeded = false;
    var singleWord = false;

    // Calculate the extra space needed to justify the text
    if (justifyWidth && metrics.totalWidthNoJustify < justifyWidth) {
        justifyNeeded = true;
        var extra = justifyWidth - metrics.totalWidthNoJustify;
        var spacesGroups = text.match(/\s/g);
        if (spacesGroups) {
            extraSpace = extra / spacesGroups.length;
        } else {
            singleWord = true;
            extraSpace = extra / (text.length - 1);
        }
    }

    // Add the offset needed for each path in case of justified text
    for (var n = 0; n < paths.length; ++n) {
        var path = paths[n];
        var character = text[n];
        path.character = character;

        if (justifyNeeded && (spaceFound || (singleWord && n !== 0))) {
            justifiedApplied = true;
            offset += extraSpace;
            spaceFound = false;
        }

        // Check if this is an space character, in justify mode we have to expand the
        //  spaces
        if (justifyNeeded && justifyWidth && /[ \t\r]/.test(character)) {
            spaceFound = true;
        }

        path.offsetY = 0;
        path.offsetX = offset;
    }

    // Render the characters in two steps, first render the stroke and then render the fill so
    //  the stroke can't go over other characters
    // If stroke width is 0 set stroke to null, otherwise a very thin line is rendered and is still visible
    this.save();
    this.strokeStyle = this.opentypeStroke;
    this.lineWidth = this.opentypeStrokeWidth;
    this.lineJoin = 'round';

    if (this._fillShadow != null && this.opentypeStrokeWidth === 0 && !this._fillPaletteColor) {
        // Canvas 2D can't draw shadow separately; it always draws shadow together with the text or stroke.
        // If there's no stroke, we use the if clause to draw shadow + fill in one step, so transparency works fine.
        // If there's stroke, we have to draw shadow+stroke, then fill (else clause). This means fill()/stroke() are called twice,
        // so alpha values stack and transparency looks wrong (with solid colors, this overdraw isn't noticeable).
        drawPaths({
                paths: paths,
                ctx: this,
                drawShadowWithFill: true,
                drawStroke: false,
                drawFill: true,
                text: text
        });
    } else {
        drawPaths({
            paths: paths,
            ctx: this,
            drawShadow: true,
            drawStroke: this.opentypeStrokeWidth > 0,
            drawFill: false,
            text: text
        });

        this.lineWidth = 0;
        drawPaths({
            paths: paths,
            ctx: this,
            drawShadow: false,
            drawStroke: false,
            drawFill: true,
            text: text
        });
    }
    this.restore();

    if (justifiedApplied) {
        metrics.width = justifyWidth + (this.opentypeStrokeWidth || 0);
        metrics.totalWidth = justifyWidth + (this.opentypeStrokeWidth || 0);
    }

    this.lastDrawnData = {
        paths: paths,
        x: x,
        y: y,
        strokeWidth: this.opentypeStrokeWidth
    };
    return metrics;
};

window._getFontSizeOpentype = function () {
    // this.font is for example `68px Arial`
    // this.trueFontSize is the current font size saved into a separated variable to avoid
    //  rounding errors introduced by each browser when assigning non-integer font sizes in
    //  CanvasRenderingContext2D.font
    return this.trueFontSize || 1 * this.font.match(/\d+/)[0];
};

window._getExactFontSizeOpentype = function () {
    return this.font.match(/\d+(\.\d+)?/g)[0];
};

window._setFontSizeOpentype = function (fontSize) {
    const declaration = {};
    // this method detructure the font declaration and assign the values in the declaration object
    fabric.parseFontDeclaration(this.font, declaration);
    this.font = `${fontSize}px ${declaration.fontFamily}`;
};

window._getFontFamilyOpentype = function () {
    var matches = this.font.match(/(\s*\d+(\.\d{1,50})?)px (.*)/);
    if (!matches) {
        return '';
    }
    return matches[matches.length - 1];
};

const spaceAtTheEndReg = new RegExp('\\s$', 'gi');
window._measureTextOpentype = function (text, kerned, justifyWidth, tracking) {
    var ffname = _getFontFamilyOpentype.call(this);
    const font = Font.hasFont(ffname) ? Font.resolve(_getFontFamilyOpentype.call(this)) : false;
    let isOpenTypeMetrics = true;
    if (!font) {
        isOpenTypeMetrics = false;
        // If available, return native method instead of error because now we want to use Itext
        if (this._measureText) {
            return this._measureText.call(this, text);
        }
        // Sometimes we get here and _measureText() is not available, so just do the normal call
        else {
            return this.measureText.call(this, text);
        }
    }

    // Don't apply kerning by default
    if (typeof kerned === 'undefined') {
        kerned = false;
    }

    this.lineSpacing = this.lineSpacing || 1;
    if (this.lineSpacing < 0) {
        this.lineSpacing = 0.01;
    }

    const ctxFontSize = _getFontSizeOpentype.call(this);
    var count = 0;
    // Just initialize max and min with ridiculously large numbers
    var maxX = -99999999,
        maxY = -99999999,
        minX = +99999999,
        minY = +99999999;
    var descent = 0,
        ascent = 0; // Max text descent and ascent
    var leftSideBearing = 0,
        rightSideBearing = 0;

    const scaleFont = (1 / font.unitsPerEm) * ctxFontSize;
    var fontHeight = font.fontHeight * scaleFont * this.lineSpacing;
    var fontAscender = font.totalAscender * scaleFont;
    var fontDescender = font.totalDescender * scaleFont;

    var leftStrokeWidth = 0,
        rightStrokeWidth = 0;

    // Only compute the stroke if there's one
    if ((this.opentypeStrokeWidth || 0) > 0) {
        // Get the left stroke width from the left-most character and the right stroke width from the right-most character
        var letterLeft = text[0];
        var letterRight = text[text.length - 1];

        if (letterLeft && letterRight) {
            const strokeWidths = computeStroke(
                letterLeft,
                letterRight,
                font,
                ctxFontSize,
                this.strokeRenderingCanvas || fabric.util.createCanvasElement(),
                this.opentypeStrokeWidth || 0,
                kerned,
                tracking,
                this.ligatures
            );
            leftStrokeWidth = strokeWidths.leftStrokeWidth;
            rightStrokeWidth = strokeWidths.rightStrokeWidth;
        }
    }

    font.forEachGlyph(
        text,
        0,
        0,
        ctxFontSize,
        {
            drawSVG: false,
            drawLayers: false,
            kerning: kerned,
            tracking: tracking,
            features: {
                liga: this.ligatures,
                rlig: this.ligatures
            }
        },
        function (glyph, glyphX, glyphY, fontSize) {
            // Code taken from opentype.js, method drawPoints() in glyph.js
            // https://github.com/nodebox/opentype.js/blob/master/src/glyph.js#L276
            var scale = (1 / (glyph.path.unitsPerEm || font.unitsPerEm)) * fontSize;
            var path = glyph.path;

            if (path.commands.length > 0 && scale) {
                var metrics = glyph.getMetrics();

                // Some fonts have a space character with path commands, but the glyph has size 0,
                //  in these cases what we actually want to do is use the advance
                if (
                    (glyph.name === 'space' || glyph.unicode === 32) &&
                    metrics.xMax === 0 &&
                    metrics.xMin === 0 &&
                    metrics.yMax === 0 &&
                    metrics.yMin === 0
                ) {
                    maxX = Math.max(glyph.advanceWidth * scale + glyphX, maxX);
                    minX = Math.min(glyphX, minX);

                    maxY = Math.max(glyphY, maxY);
                    minY = Math.min(glyphY, minY);
                } else {
                    maxX = Math.max(metrics.xMax * scale + glyphX, maxX);
                    minX = Math.min(metrics.xMin * scale + glyphX, minX);

                    maxY = Math.max(metrics.yMax * scale + glyphY, maxY);
                    minY = Math.min(metrics.yMin * scale + glyphY, minY);

                    if (count == 0) {
                        leftSideBearing = metrics.leftSideBearing * scale;
                    }
                    rightSideBearing = metrics.rightSideBearing * scale;

                    descent = Math.max(descent, glyph.descent * scale);
                    ascent = Math.max(ascent, glyph.ascent * scale);
                }
            } else if (!scale) {
                // Some fonts doesn't contain width information so just add the glyph x position
                maxX = Math.max(glyphX, maxX);
                minX = Math.min(glyphX, minX);

                maxY = Math.max(glyphY, maxY);
                minY = Math.min(glyphY, minY);
            } else {
                // If scale was properly calculated (some fonts doesn't have the unitsPerEm in some glyphs) use the advanceWidth
                maxX = Math.max(glyph.advanceWidth * scale + glyphX, maxX);
                minX = Math.min(glyphX, minX);

                maxY = Math.max(glyphY, maxY);
                minY = Math.min(glyphY, minY);
            }

            ++count;
        }
    );

    // Empty text has no width or height
    if (!text) {
        maxX = maxY = minX = minY = 0;
    }

    var totalWidth = maxX - minX;
    var totalHeight = maxY - minY;

    // Never return width 0, it screws up everything!
    if (totalWidth == 0) {
        totalWidth = 1;
    }
    if (totalHeight == 0) {
        totalHeight = 1;
    }

    // Calculate width with this method we just use the above code to compute the exact text height, we need
    //  this method to calculate text width to consider the font kerning that the above method ignores
    var width;
    font.forEachGlyph(
        text + ' ',
        0,
        0,
        ctxFontSize,
        {
            drawSVG: false,
            drawLayers: false,
            kerning: kerned,
            tracking: tracking,
            features: {
                liga: this.ligatures,
                rlig: this.ligatures
            }
        },
        function (glyph, x, y) {
            width = x;
        }
    );

    var totalWidthNoJustify = totalWidth;
    if (justifyWidth) {
        if (text.length > 1 && spaceAtTheEndReg.exec(text) == null) {
            width = totalWidth = justifyWidth;
        }
    }

    // `width` and `totalWidth` can have approximately the same value or a really different one. Some fonts doesn't have the proper bounding
    //  box information for each glyph, it can be larger or smaller than it should, so the first method to measure the font width which is
    //  the most accurate gives the `totalWidth`. But we also need the normal width returned by the font information because this width
    //  can be calculated with or without kerning which is important to handle cursor positioning and also because the rendering method used
    //  by fabric.js needs the kerning width between the characters.
    return {
        width: width,
        height: totalHeight + (this.opentypeStrokeWidth || 0),
        heightNoStroke: totalHeight,
        totalWidth: totalWidth,
        totalWidthNoJustify: totalWidthNoJustify,
        minY: minY,
        maxY: maxY,
        minX: minX,
        maxX: maxX,
        fontHeight: fontHeight,
        fontAscender: fontAscender,
        fontDescender: fontDescender,
        descent: descent,
        ascent: ascent,
        leftSideBearing: leftSideBearing,
        rightSideBearing: rightSideBearing,
        leftStrokeWidth: leftStrokeWidth,
        rightStrokeWidth: rightStrokeWidth,
        isOpenTypeMetrics: isOpenTypeMetrics
    };
};

const _dimensionAffectingProps = [
    'maxWidth',
    'maxHeight',
    'text',
    'fontSize',
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'minFontSize',
    'maxFontSize',
    'tracking',
    'ligatures',
    'lineSpacing',
    'textAlign',
    'multiline',
    'opentypeStrokeWidth',
    'verticalText'
];
let wordsCache = {};
let disableLigaturesCache = {};

fabric.OpentypeIText = fabric.util.createClass(fabric.Object, {
    type: 'opentype-itext',

    _dimensionAffectingProps: _dimensionAffectingProps,
    cacheProperties: fabric.Object.prototype.cacheProperties.concat(
        _dimensionAffectingProps,
        'opentypeStroke',
        'fillPattern',
        'textureScale',
        'shadowType',
        'shadowOffset',
        'shadowAngle',
        'shadowBlur',
        'shadowFill',

    ),

    width: 1,
    height: 1,
    maxWidth: 1,
    maxHeight: 1,
    minFontSize: 1,
    maxFontSize: 1,

    text: '',
    fontFamily: 'Arial',
    fontSize: 1,
    multiline: false,
    verticalText: false,

    /**
     * Font tracking amount(AKA letter spacing) Letter Spacing is usually declared in ems.
     * Graphics Software uses the term Tracking, and multiplies the ems by 1000
     *  (http: //stackoverflow.com/a/36612356/6465140) to avoid working in decimals.
     */
    tracking: 0,

    // Line spacing as percent (0 to 1 is 0 to 100%)
    lineSpacing: 1,
    ligatures: true,
    // This is automatically set to true when we detect the font can't be rendered with ligatures
    _ligaturesNotSupported: false,

    flatCharHeightCache: {},

    // This 3 parameters make sure the computed text height matches the element
    //  height
    lineHeight: 1.16,
    _fontSizeMult: 1,
    _fontSizeFraction: 0,
    _textData: null,

    _fillPatternNeedsUpdate: true,
    __fillPatternImage: null,
    __fillPattern: null,
    _lastDrawnData: {},
    textureScale: 100,
    _fillGradient: null,

    // Stroke
    opentypeStrokeWidth: 0,
    opentypeStroke: '#000000',
    strokeRenderingCanvas: null,

    CACHE_FONT_SIZE: 200,

    initialize: function (text, options) {
        // Call super constructor
        this.callSuper('initialize', options);
        this.text = text;

        // Make sure we have a valid version
        options.version = options.version || fabric.BoundedText.prototype.version;

        // Create a canvas to use to measure strokes
        this.strokeRenderingCanvas = fabric.util.createCanvasElement();
        // Uncomment the following line to see the canvas to debug it
        // document.getElementsByTagName('body')[0].append(this.strokeRenderingCanvas);

        this._setWidthHeight();
    },

    _measureTextOpenType: function (ctx, word, kerned, justifyWidth, tracking) {
        try {
            return {
                metric: window._measureTextOpentype.call(ctx, word, kerned, justifyWidth, tracking),
                disableLigatures: false
            };
        } catch (error) {
            // Errors like "lookupType: 7 - substFormat: 1 is not yet supported" are thrown on some fonts when ligatures are enabled,
            //  if this is the case disable ligatures for this font
            if (!this._ligaturesNotSupported && error && error.message.indexOf('is not yet supported') > -1) {
                this._ligaturesNotSupported = true;
                console.warn('Disabling ligatures for font ' + this.fontFamily);

                // Disable ligatures and measure again without ligatures
                ctx.ligatures = false;
                return {
                    metric: window._measureTextOpentype.call(ctx, word, kerned, justifyWidth, tracking),
                    disableLigatures: true
                };
            } else {
                throw error;
            }
        }
    },

    _splitTextIntoLines: function (text, tracking, maxWidth, fontSize) {
        const words = text.split(' ');
        const lines = [];
        let currentLine = words[0];

        for (let n = 1; n < words.length; n++) {
            const word = words[n];
            const metrics = this._getTextMetrics(currentLine + ' ' + word, tracking);
            const scale = fontSize / this.CACHE_FONT_SIZE;

            if (metrics.textWidth * scale < maxWidth) {
                currentLine += ' ' + word;
            } else {
                lines.push(currentLine);
                currentLine = word;
            }
        }

        lines.push(currentLine);
        this.splitText = lines.length > 1;
        return lines.join('\n');
    },

    /**
     * Generate the cache key for the given canvas context. The key is generated from all the properties that can affect text metrics.
     * @param {CanvasRenderingContext2D} ctx canvas context
     * @param {number} tracking font tracking
     * @param {number} ligatures wether to use font ligatures or not
     * @returns cache key
     */
    _getCtxCacheKey: function (ctx, tracking, ligatures) {
        return `${ctx.font}:${ctx.lineSpacing}:${ctx.opentypeStrokeWidth}:${tracking}:${!!ligatures}`;
    },

    /**
     * Combine metrics from multiple words in a single line metrics as if the words were joined
     * @param {string[]} words array of words
     * @param {Metric[]} metrics array of metrics for each of the given words, must be the same length as the array of words and with the same order
     * @param {number} tracking font tracking amount
     * @param {number} fontSize font size used to generate the given metrics
     * @param {number} strokeWidth desired stroke width to use for this line metric
     * @returns line metric
     */
    _joinWordsMetrics: function (words, metrics, tracking, fontSize, strokeWidth) {
        const lineMetric = {
            width: 0,
            totalWidth: 0,
            height: fabric.util.array.max(metrics, 'height') + strokeWidth,
            heightNoStroke: fabric.util.array.max(metrics, 'heightNoStroke'),
            minY: fabric.util.array.min(metrics, 'minY'),
            maxY: fabric.util.array.max(metrics, 'maxY'),
            totalWidthNoJustify: 0,
            fontHeight: metrics[0].fontHeight,
            fontAscender: metrics[0].fontAscender,
            fontDescender: metrics[0].fontDescender,
            descent: fabric.util.array.max(metrics, 'descent'),
            ascent: fabric.util.array.max(metrics, 'ascent'),
            leftSideBearing: metrics[0].leftSideBearing,
            rightSideBearing: metrics[metrics.length - 1].rightSideBearing,
            leftStrokeWidth: isSpaceChar(words[0]) ? 0 : strokeWidth / 2,
            rightStrokeWidth: isSpaceChar(words[words.length - 1]) ? 0 : strokeWidth / 2
        };

        for (let index = 0; index < words.length; index++) {
            const word = words[index];
            const metric = metrics[index];

            lineMetric.width += metric.width;
            lineMetric.totalWidth += metric.totalWidth;

            // If this is a space character
            if (isSpaceChar(word)) {
                const prevMetric = metrics[index - 1];
                const postMetric = metrics[index + 1];
                const prevWord = words[index - 1];

                // Add the extra width from the bearing of the characters around this space character
                let extraWidth =
                    (prevMetric ? prevMetric.rightSideBearing : 0) + (postMetric ? postMetric.leftSideBearing : 0);

                // Add the extra width from the font tracking
                extraWidth += (index === 0 || isSpaceChar(prevWord) ? 1 : 2) * (tracking / 1000) * fontSize;

                lineMetric.totalWidth += extraWidth;
                lineMetric.width += extraWidth;
            }
        }
        lineMetric.totalWidthNoJustify = lineMetric.totalWidth;

        return lineMetric;
    },

    /**
     * Compute metrics for the given text. The text width returned by this method does not include the stroke width.
     * @param {string} text text to measure (can have newlines)
     * @param {number} tracking font tracking amount
     * @returns text metric
     */
    _getTextMetrics: function (text, tracking) {
        // Replacing all \r\n for \n and then splitting by \n is MUCH faster than splitting with a regex expression like /\r?\n/
        const textLines = text.replace(/\r\n/g, '\n').split('\n');
        const ctx = this.getMeasuringContext();

        // Do not measure text with store, the stroke is a fixed width added to the characters that does not vary with font size
        ctx.opentypeStrokeWidth = 0;

        const ctxCacheKey = this._getCtxCacheKey(ctx, tracking, ctx.ligatures);
        if (!wordsCache[ctxCacheKey]) {
            wordsCache[ctxCacheKey] = {};
        }

        if (disableLigaturesCache[ctxCacheKey]) {
            ctx.ligatures = false;
            this._ligaturesNotSupported = true;
        }

        // Measure each text line individually
        const lineMetrics = [];
        for (const line of textLines) {
            // Split lines into words and spaces, so a sentence like " This is a big   space!" will be converted to:
            //  [" ", "This", " ", "is", " ", "a", " ", "big", " ", " ", " ", "space", "!"]
            const words = splitWords(line);
            const wordsMetrics = [];

            for (const word of words) {
                // Only measure the text if the measurement for this word is not cached already
                if (!wordsCache[ctxCacheKey][word]) {
                    const { metric, disableLigatures } = this._measureTextOpenType(ctx, word, true, null, tracking);
                    disableLigaturesCache[ctxCacheKey] = disableLigatures;

                    if (disableLigatures) {
                        ctx.ligatures = false;
                    }

                    // Don't store metrics that are not from opentype (when font is still not loaded)
                    if (metric && metric.isOpenTypeMetrics) {
                        wordsCache[ctxCacheKey][word] = metric;
                    }

                    wordsMetrics.push(metric);
                } else {
                    wordsMetrics.push(wordsCache[ctxCacheKey][word]);
                }
            }

            // Convert the metrics from each word into a metric from a single line of text
            const lineMetric = this._joinWordsMetrics(
                words,
                wordsMetrics,
                tracking,
                this.CACHE_FONT_SIZE,
                this.opentypeStrokeWidth
            );
            lineMetrics.push(lineMetric);
        }

        // Add the heights of each line
        let textHeight = 0;
        for (const metric of lineMetrics) {
            textHeight += metric.fontHeight;
        }
        // Find the line with the maximum width
        let textWidth = fabric.util.array.max(lineMetrics, 'totalWidth');

        let leftStrokeWidth = lineMetrics.some((m) => m.leftStrokeWidth > 0) ? this.opentypeStrokeWidth / 2 : 0,
            rightStrokeWidth = lineMetrics.some((m) => m.rightStrokeWidth > 0) ? this.opentypeStrokeWidth / 2 : 0;

        return {
            lineMetrics: lineMetrics,
            textLines: textLines,
            textWidth: textWidth,
            textHeight: textHeight,
            leftStrokeWidth: leftStrokeWidth,
            rightStrokeWidth: rightStrokeWidth
        };
    },

    isFontSizeBelowMinimum(text, tracking, maxWidth, maxHeight) {
        let { textWidth, textHeight, leftStrokeWidth, rightStrokeWidth } = this._getTextMetrics(text, tracking);
        if (this.allowEdit && this.calculateDimensions) {
            const scaleLimit = this._constraintScaleByProjection(this.bbWidth, this.bbHeight, textWidth, textHeight);
            maxWidth = scaleLimit.width;
            maxHeight = scaleLimit.height;
        }
        // Calculate the proper font size knowing that we made our measurements using CACHE_FONT_SIZE
        const scaleX = (maxWidth - leftStrokeWidth - rightStrokeWidth) / textWidth;
        const scaleY = maxHeight / textHeight;
        let scaling = Math.min(scaleX, scaleY);
        let fontSize = scaling * this.CACHE_FONT_SIZE;
        return fontSize < this.minFontSize;
    },

    /**
     * Fit text within the given bounds
     * @param {string} text text to fit
     * @param {number} tracking font tracking amount
     * @param {number} maxWidth max text width
     * @param {number} maxHeight max text height
     * @returns text metric
     */
    _getBoundedTextMetrics: function (text, tracking, maxWidth, maxHeight) {
        this.splitText = false;
        let { lineMetrics, textLines, textWidth, textHeight, leftStrokeWidth, rightStrokeWidth } = this._getTextMetrics(
            text,
            tracking
        );

        if (this.allowEdit && this.calculateDimensions) {
            const scaleLimit = this._constraintScaleByProjection(this.bbWidth, this.bbHeight, textWidth, textHeight);
            maxWidth = scaleLimit.width;
            maxHeight = scaleLimit.height;
        }
        // Calculate the proper font size knowing that we made our measurements using CACHE_FONT_SIZE
        const scaleX = (maxWidth - leftStrokeWidth - rightStrokeWidth) / textWidth;
        const scaleY = maxHeight / textHeight;
        let scaling = Math.min(scaleX, scaleY);
        let fontSize = scaling * this.CACHE_FONT_SIZE;
        let deletedChars = false;
        let deletedCharOnIteration = false;
        // Limit font size
        if (fontSize > this.maxFontSize) {
            scaling *= this.maxFontSize / fontSize;
            fontSize = this.maxFontSize;
        } else if (fontSize < this.minFontSize) {
            let renderText = text;
            let count = 0,
                scalingFactorHeight = 0,
                scalingFactorWidth = 0;

            do {
                let removeChar = true;

                // For multiline text, remove lines until the text fits, then add the last removed line before
                //  the text fitted. This way we can quickly remove a chunk of text that goes outside the bounds
                //  without needing to remove one character at a time, making this method much faster when there
                //  is a lot of text that won't fit in the bounds
                if (this.multiline && count === 1 && (!this.allowEdit || this._allowEditWithoutRotation())) {
                    let splittedText = this._splitTextIntoLines(
                            text,
                            tracking,
                            maxWidth - this.opentypeStrokeWidth,
                            this.minFontSize
                        ),
                        lastLine = '',
                        sy = 1,
                        sx = 1;

                    do {
                        const index = splittedText.lastIndexOf('\n');
                        if (index > -1) {
                            lastLine = splittedText.slice(index + 1);
                            splittedText = splittedText.slice(0, index);
                        } else {
                            break;
                        }

                        const metrics = this._getTextMetrics(splittedText, tracking);
                        sx = (maxWidth - metrics.leftStrokeWidth - metrics.rightStrokeWidth) / metrics.textWidth;
                        sy = maxHeight / metrics.textHeight;
                    } while (Math.min(sy, sx) * this.CACHE_FONT_SIZE < this.minFontSize && splittedText);

                    // Join the text we know fits in a single line
                    text = splittedText;
                    if (lastLine) {
                        text += '\n' + lastLine;
                    } else {
                        removeChar = false;
                    }
                }

                if (count > 0 && removeChar) {
                    // If we need a font size lower than the minimum, we need to remove characters
                    //  until the text fits. Remove one character at a time.
                    text = text.slice(0, text.length - 1);
                    deletedChars = true;
                    deletedCharOnIteration = true;
                } else {
                    deletedCharOnIteration = false;
                }

                if (
                    this.multiline &&
                    (!this.allowEdit ||
                        this._allowEditWithoutRotation() ||
                        (this._allowEditWithRotation() && count == 0))
                ) {
                    fontSize = this.minFontSize;
                    renderText = this._splitTextIntoLines(
                        text,
                        tracking,
                        maxWidth - this.opentypeStrokeWidth,
                        fontSize
                    );
                } else {
                    renderText = text;
                }

                const metrics = this._getTextMetrics(renderText, tracking);
                lineMetrics = metrics.lineMetrics;
                textLines = metrics.textLines;
                textWidth = metrics.textWidth;
                textHeight = metrics.textHeight;
                leftStrokeWidth = metrics.leftStrokeWidth;
                rightStrokeWidth = metrics.rightStrokeWidth;

                if (
                    this.allowEdit &&
                    this.calculateDimensions &&
                    (deletedCharOnIteration || (this.multiline && count == 0))
                ) {
                    const scaleLimit = this._constraintScaleByProjection(
                        this.bbWidth,
                        this.bbHeight,
                        textWidth,
                        textHeight
                    );
                    maxWidth = scaleLimit.width;
                    maxHeight = scaleLimit.height;
                }

                scalingFactorWidth = (maxWidth - leftStrokeWidth - rightStrokeWidth) / textWidth;
                scalingFactorHeight = maxHeight / textHeight;
                scaling = Math.min(scalingFactorHeight, scalingFactorWidth);
                fontSize = scaling * this.CACHE_FONT_SIZE;
                ++count;
            } while (fontSize < this.minFontSize && text.length);

            if (fontSize > this.maxFontSize) {
                scaling = this.maxFontSize / this.CACHE_FONT_SIZE;
                fontSize = scaling * this.CACHE_FONT_SIZE;
            }
        }

        // Apply the scaling to all metrics
        for (const metric of lineMetrics) {
            for (const key in metric) {
                // Stroke doesn't need scaling as it's a constant width independent of font size
                if (key !== 'leftStrokeWidth' && key !== 'rightStrokeWidth') {
                    metric[key] = metric[key] * scaling;
                }
            }
        }

        // Add stroke width for all metrics
        for (const metric of lineMetrics) {
            metric.totalWidth += metric.leftStrokeWidth + metric.rightStrokeWidth;
            metric.totalWidthNoJustify += metric.leftStrokeWidth + metric.rightStrokeWidth;
        }

        return {
            fontSize,
            textLines,
            metrics: lineMetrics,
            textWidth: textWidth * scaling + leftStrokeWidth + rightStrokeWidth,
            textHeight: textHeight * scaling + this.opentypeStrokeWidth,
            deletedChars,
            maxWidth,
            maxHeight
        };
    },

    _allowEditWithRotation: function () {
        return this.allowEdit && this.angle % 360 !== 0;
    },

    _allowEditWithoutRotation: function () {
        return this.allowEdit && this.angle % 360 === 0;
    },

    _constraintScaleByProjection: function (boundingBoxWidth, boundingBoxHeight, textWidth, textHeight) {
        const boundingBoxLeft = 0;
        const boundingBoxTop = 0;
        const boundingBoxAngle = 0;

        const bbAngleRad = fabric.util.degreesToRadians(boundingBoxAngle);

        // Get the inner rectangle's center position in the coordinate system of the bounding box.
        // This is done by shifting its center relative to the bounding box and then rotating accordingly.
        const dx = this.left - boundingBoxLeft;
        const dy = this.top - boundingBoxTop;
        const cosBB = Math.cos(-bbAngleRad);
        const sinBB = Math.sin(-bbAngleRad);
        const localX = dx * cosBB - dy * sinBB;
        const localY = dx * sinBB + dy * cosBB;

        // Determine how much space is available from the inner rectangle's center to the edges of the bounding box.
        const halfBBWidth = boundingBoxWidth / 2;
        const halfBBHeight = boundingBoxHeight / 2;
        const availX = halfBBWidth - Math.abs(localX);
        const availY = halfBBHeight - Math.abs(localY);

        // Find out how the inner rectangle is rotated relative to the bounding box.
        const innerAngleRad = fabric.util.degreesToRadians(this.angle);
        const relativeAngle = innerAngleRad - bbAngleRad;
        const absCos = Math.abs(Math.cos(relativeAngle));
        const absSin = Math.abs(Math.sin(relativeAngle));

        // Get half the inner rectangle's dimensions.
        let innerRectWidth = textWidth;
        let innerRectHeight = textHeight;

        const halfW = innerRectWidth / 2;
        const halfH = innerRectHeight / 2;

        // Calculate the largest scale factor that keeps the inner rectangle fully inside the bounding box.
        // This factor is limited by both the horizontal and vertical available space.
        const sX = availX / (halfW * absCos + halfH * absSin);
        const sY = availY / (halfW * absSin + halfH * absCos);

        // Compute the maximum width and height after applying the scale factor.
        const maxWidth = innerRectWidth * sX;
        const maxHeight = innerRectHeight * sY;
        // Return the computed scale factor and the new dimensions.
        return {
            scaleX: sX,
            scaleY: sY,
            maxScale: Math.min(sX, sY),
            width: maxWidth,
            height: maxHeight
        };
    },

    /**
     * Return a context for measurement of text string.
     * if created it gets stored for reuse
     * @param {String} text Text string
     * @param {Object} [options] Options object
     */
    getMeasuringContext: function () {
        // if we did not return we have to measure something.
        if (!fabric._measuringContext) {
            fabric._measuringContext =
                (this.canvas && this.canvas.contextCache) || fabric.util.createCanvasElement().getContext('2d');
        }
        const ctx = fabric._measuringContext;

        ctx.lineSpacing = this.lineSpacing;
        ctx.ligatures = this._ligaturesNotSupported ? false : this.ligatures;

        // Prepare the measuring context
        ctx.font = fabric.Text.prototype._getFontDeclaration.call(
            this,
            {
                fontFamily: this.fontFamily,
                fontSize: this.CACHE_FONT_SIZE,
                fontWeight: this.fontWeight,
                fontStyle: this.fontStyle
            },
            true
        );
        ctx.trueFontSize = this.CACHE_FONT_SIZE;
        ctx.strokeRenderingCanvas = this.strokeRenderingCanvas;
        return ctx;
    },

    _setWidthHeight: function () {
        this._textData = this._getBoundedTextMetrics(this.text, this.tracking, this.maxWidth, this.maxHeight);

        this.width = this.textAlign === 'justify' ? this.maxWidth : this._textData.textWidth;
        this.height = this._textData.textHeight;
        this.fontSize = this._textData.fontSize;
        this.deletedChars = this._textData.deletedChars;
        if (this.allowEdit) {
            this.maxWidth = this._textData.maxWidth;
            this.maxHeight = this._textData.maxHeight;
        }
    },

    /**
     * Sets specified property to a specified value
     * @param {String} key
     * @param {*} value
     * @chainable
     */
    _set: function (key, value) {
        if (key === 'fontFamily') {
            // Clear characters height cache when changing font
            this.flatCharHeightCache = {};
            this._ligaturesNotSupported = false;
        } else if (key === 'fillPattern') {
            this._fillPatternNeedsUpdate = true;
        } else if (key === 'fill' || key === 'color') {
            if (typeof value === 'object') {
                const colorObj = fabric.util.createColorType(value);
                value = colorObj ? colorObj : value;
            }
        }

        this.callSuper('_set', key, value);

        if (this._dimensionAffectingProps.indexOf(key) > -1) {
            this._setWidthHeight();
        }

        return this;
    },

    getTextPaths: function (asSvg = false) {
        const applyPathsOffset = (paths, extraOffsetX = 0, extraOffsetY = 0) => {
            for (let n = 0; n < paths.length; ++n) {
                const path = paths[n];
                const offsetX = path.offsetX + extraOffsetX;
                const offsetY = path.offsetY + extraOffsetY;

                for (let i = 0; i < path.commands.length; i += 1) {
                    const cmd = path.commands[i];
                    if (cmd.type === 'M' || cmd.type === 'L') {
                        cmd.x += offsetX;
                        cmd.y += offsetY;
                    } else if (cmd.type === 'C') {
                        cmd.x += offsetX;
                        cmd.y += offsetY;
                        cmd.x1 += offsetX;
                        cmd.y1 += offsetY;
                        cmd.x2 += offsetX;
                        cmd.y2 += offsetY;
                    } else if (cmd.type === 'Q') {
                        cmd.x += offsetX;
                        cmd.y += offsetY;
                        cmd.x1 += offsetX;
                        cmd.y1 += offsetY;
                    }
                }
            }

            return paths;
        };

        const containerW = this.maxWidth;
        const containerH = this.maxHeight;
        const svgPaths = [];

        if (this._lastDrawnData && Object.keys(this._lastDrawnData).length) {
            const keys = Object.keys(this._lastDrawnData);
            for (let k = 0; k < keys.length; k++) {
                const key = keys[k];
                const data = this._lastDrawnData[key];

                // Calculate the X offset to center the text in the container and then correct it if needed
                //  according to the current text align
                let offsetX = -data.x + (containerW - this.width) / 2 - (-data.x - this.width / 2);
                if (this.textAlign === 'left') {
                    offsetX = offsetX - (containerW - this.width) / 2;
                } else if (this.textAlign === 'right') {
                    offsetX = offsetX + (containerW - this.width) / 2;
                }

                const offsetY = data.y + (containerH - this.height) / 2 - (data.y - this.height / 2);
                const paths = applyPathsOffset(data.paths, offsetX, offsetY);

                for (let n = 0; n < paths.length; n++) {
                    const path = paths[n];
                    svgPaths.push(asSvg ? path.toSVG(8) : path.toPathData(8));
                }
            }

            return {
                paths: svgPaths,
                strokeWidth: this._lastDrawnData[0].strokeWidth,
                width: containerW,
                height: containerH,
                textData: this._textData
            };
        }
        return null;
    },

    _renderBackground: function (ctx) {
        // Correctly clear the entire cache canvas to prevent weird leftovers when text goes
        //  outside bounds
        let dim = {
            x: ctx.canvas.width,
            y: ctx.canvas.height
        };
        ctx.clearRect(-dim.x / 2, -dim.y / 2, dim.x, dim.y);

        if (!this.backgroundColor) {
            return;
        }
        dim = this._getNonTransformedDimensions();
        ctx.fillStyle = this.backgroundColor;

        ctx.fillRect(-dim.x / 2, -dim.y / 2, dim.x, dim.y);
        // if there is background color no other shadows
        // should be casted
        this._removeShadow(ctx);
    },

    /**
     * @private
     * @param {CanvasRenderingContext2D} ctx Context to render on
     */
    _render: function (ctx) {
        ctx.strokeRenderingCanvas = this.strokeRenderingCanvas;

        // Render text
        this._renderTextCommon(ctx);

        // Debugging bounding box
        //this.renderBoundingBox(ctx);
    },

    renderBoundingBox: function (ctx) {
        ctx.save();

        ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
        ctx.lineWidth = 1;

        const width = this.width;
        const height = this.height;

        ctx.strokeRect(
            -width / 2, // x: el origen está en el centro
            -height / 2, // y: el origen está en el centro
            width,
            height
        );
        ctx.restore();
    },

    /**
     * Gets the height of a character without ascent or descent like 'x', 'a', etc.
     * We use this to allow vertical align along these characters instead of the total
     *  text height. This method is only used in BoundedText.js
     */
    getFlatCharacterHeight: function () {
        const referenceChar = 'x';
        const ctx = this.getMeasuringContext();
        const scaling = this.fontSize / this.CACHE_FONT_SIZE;
        let height = 0;

        // Check if we have this character height already computed
        if (
            this.flatCharHeightCache[referenceChar] &&
            this.flatCharHeightCache[referenceChar].font === ctx.font &&
            this.flatCharHeightCache[referenceChar].strokeWidth === ctx.opentypeStrokeWidth &&
            this.textAlign === this.flatCharHeightCache[referenceChar].textAlign
        ) {
            height = this.flatCharHeightCache[referenceChar].height;
        } else {
            // Compute the character height and store it in cache for the next time
            const { metric } = this._measureTextOpenType(ctx, referenceChar, true, null, this.tracking);
            this.flatCharHeightCache[referenceChar] = {};
            this.flatCharHeightCache[referenceChar].font = ctx.font;
            this.flatCharHeightCache[referenceChar].strokeWidth = ctx.opentypeStrokeWidth;
            this.flatCharHeightCache[referenceChar].textAlign = this.textAlign;

            this.flatCharHeightCache[referenceChar].height = metric.heightNoStroke;
            height = metric.heightNoStroke;
        }

        height = height * scaling;
        return height;
    },

    _updatePatternFill: function (ctx) {
        if (!this.fillPattern || !ctx) {
            this._fillPattern = null;
            this._fillPatternImage = null;
            return;
        }

        const targetWidth = this.width;
        const targetHeight = this.getFlatCharacterHeight();
        let imgWidth = this.fillPattern.source.width;
        let imgHeight = this.fillPattern.source.height;
        const scaleX = targetWidth / imgWidth;
        const scaleY = targetHeight / imgHeight;
        let baseScale = fabric.util.toFixed(Math.min(scaleX, scaleY), 4);
        let factorScale = (this.textureScale || 100) / 100;
        let finalScale = baseScale * factorScale;
        this._fillPattern = this.fillPattern.toLive(ctx);

        const transform = {
            scaleX: finalScale,
            scaleY: finalScale,
            skewX: 0,
            skewY: 0,
            angle: 0,
            translateX: -targetWidth / 2,
            translateY: -targetHeight / 2
        };

        const matrix = fabric.util.composeMatrix(transform);
        const matrixValues = matrix.slice(0, 6);
        this.fillPattern.patternTransform = matrixValues;

        this._fillPatternImage = {
            patternTransform: matrixValues
        };
    },

    _updateGradientFill: function (ctx) {
        const gradient = this.fill || this.color;
        if (!gradient || typeof gradient !== 'object' || gradient === null || !(gradient instanceof fabric.Gradient)) {
            this._fillGradient = null;
            ctx._fillGradient = null;
            ctx._fillGradientTransform = null;
            return;
        }
        if (!gradient.toLive) {
            console.warn('Gradient Error', gradient);
            return;
        }
        ctx._fillGradient = gradient;
        const offsetX = -this.width / 2;
        const offsetY = -this.height / 2;
        ctx._fillGradientTransform = [this.width, 0, 0, this.height, offsetX, offsetY];
    },

    _updatePaletteColor: function (ctx) {
        const paletteColor = this.fill || this.color;
        if (
            !paletteColor ||
            paletteColor === null ||
            typeof paletteColor !== 'object' ||
            !(paletteColor instanceof fabric.Palette)
        ) {
            this._fillPaletteColor = null;
            ctx._fillPaletteColor = null;
            return;
        }
        if (!paletteColor.toLive) {
            console.warn('Palette Color Error', paletteColor);
            return;
        }
        ctx._fillPaletteColor = paletteColor;
    },

    calculateShadowFill: function () {
        var canvas = this.canvas, scaling,
            multX = (canvas && canvas.viewportTransform[0]) || 1,
            multY = (canvas && canvas.viewportTransform[3]) || 1;
        scaling = this.getObjectScaling();
        if (canvas && canvas._isRetinaScaling()) {
            multX *= fabric.devicePixelRatio;
            multY *= fabric.devicePixelRatio;
        }

        //calculate offsets
        const shadowOffset = this.shadowOffset || 0;
        const reference = this.fontSize * 0.2;
        const distance = reference * (shadowOffset / 100);
        const angleRad = (this.shadowAngle + (this.angle || 0)) * Math.PI / 180;
        const shadowOffsetX = (distance * multX * scaling.scaleX) * Math.cos(angleRad);
        const shadowOffsetY = (distance * multY * scaling.scaleY) * Math.sin(angleRad);

        //calculate blur
        const blurReference = this.fontSize * 0.8;
        const shadowBlur = blurReference * ((this.shadowBlur || 0) / 100);
        const blur = shadowBlur * fabric.browserShadowBlurConstant *
            (multX + multY) * (scaling.scaleX + scaling.scaleY) / 4;

        return {
            offsetX: shadowOffsetX || 0,
            offsetY: shadowOffsetY || 0,
            blur: blur || 0
        };
    },

    _updateShadowFill: function (ctx) {
        if(this.shadowType == null) {
            ctx._fillShadow = null;
            return;
        }

        const shadowFill = this.calculateShadowFill();
        ctx._fillShadow = {
            offsetX: shadowFill.offsetX,
            offsetY: shadowFill.offsetY,
            blur: shadowFill.blur,
            color: this.shadowFill || ''
        };
    },

    _renderTextCommon: function (ctx) {
        let metric;
        this._lastDrawnData = {};

        const { textLines, metrics, textHeight } = this._textData;

        ctx.opentypeStroke = this.opentypeStroke;
        ctx.opentypeStrokeWidth = this.opentypeStrokeWidth;
        ctx.lineSpacing = this.lineSpacing;
        ctx.ligatures = this._ligaturesNotSupported ? false : this.ligatures;
        ctx.font = fabric.Text.prototype._getFontDeclaration.call(
            this,
            {
                fontFamily: this.fontFamily,
                fontSize: this.fontSize,
                fontWeight: this.fontWeight,
                fontStyle: this.fontStyle
            },
            false
        );
        ctx.fillStyle = this.fill || this.color;
        ctx.trueFontSize = this.fontSize;

        this._updateShadowFill(ctx);
        this._updatePatternFill(ctx);
        this._updatePaletteColor(ctx);
        ctx._fillPattern = this._fillPattern;
        ctx._fillPatternImage = this._fillPatternImage;

        let lineHeights = 0;
        let left = 0;
        let lineHeightsStory = [];

        // When we have a single line 'this.getTextWidth()' has the same length as our only line of text so
        //  no need to calculate it twice (once here and once inside the for loop)
        if (textLines.length === 1 && this.textAlign !== 'justify') {
            metric = metrics[0];
            left = -this.width / 2;
        } else {
            // This is the width that the element will have after rendering the text. This width is the bigger
            //  width of all the lines in the text.
            left = -this.width / 2;
        }

        const totalLinesHeight = textHeight;
        // For single line text, make the text height the height of a character without ascent or descent so the
        //  text is aligned along this characters
        const totalTextHeight =
            metrics.length === 1
                ? this.getFlatCharacterHeight()
                : metrics[0].maxY + metrics[0].fontHeight * (metrics.length - 1);

        const x = (totalLinesHeight - totalTextHeight) / 2;
        const offsetY = totalLinesHeight / 2 - x - metrics[0].fontHeight * (metrics.length - 1);

        this._updateGradientFill(ctx);

        for (let i = 0, len = textLines.length; i < len; i++) {
            let leftOffset = 0;

            if (textLines.length !== 1 || this.textAlign === 'justify') {
                metric = metrics[i];
            }
            if (this.textAlign === 'center') {
                leftOffset = (this.width - metric.totalWidth) / 2;
            }
            if (this.textAlign === 'right') {
                leftOffset = this.width - metric.totalWidth;
            }

            leftOffset += metric.leftStrokeWidth;
            const heightOfLine = metric.fontHeight;
            lineHeightsStory[i] = heightOfLine;

            if (i > 0) {
                lineHeights += lineHeightsStory[i - 1];
            }

            this._renderTextLine(
                ctx,
                textLines[i],
                this.textAlign === 'justify',
                left + leftOffset - metric.leftSideBearing,
                lineHeights + offsetY - this.opentypeStrokeWidth / 2,
                i,
                metric
            );
        }
    },

    _renderTextLine: function (ctx, lineText, justify, left, top, lineIndex, lineMetrics) {
        ctx.save();

        // Pass lineMetrics to canvas context so they don't need to be recalculated
        ctx.lineMetrics = lineMetrics;
        const info = window._fillTextOpentype.call(
            ctx,
            lineText,
            left,
            top,
            justify ? this.maxWidth : null,
            this.tracking
        );
        this._lastDrawnData[lineIndex] = Object.assign({}, ctx.lastDrawnData, {
            width: this.width,
            height: this.height
        });
        ctx.lineMetrics = null;

        ctx.restore();
        return info;
    }
});
